import { Box, Stack } from "@mui/system";
import {
  Autocomplete,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DataAccuracyFilter from "./DataAccuracyFilter";
import { useSelector, useDispatch } from "react-redux";
import {
  setDisplayMode,
  setHideDone,
  setSelectedDataAccuracy,
} from "../../../../../features/dataAccuracySlice";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";

function DataAccuracyFilterCard() {
  const clearSelectedDataAccuracy = () => {
    dispatch(setSelectedDataAccuracy(null)); // Clear the selected DataAccuracy
  };

  const dispatch = useDispatch();

  const { dataAccruracyData, selectedDataAccuracy, displayMode, hideDone } =
    useSelector((state: any) => state.dataAccuracy);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchValue = searchParams.get("search");
  const displayParam = searchParams.get("display");
  const doneIssuesParam = searchParams.get("doneIssues");

  useEffect(() => {
    if (searchValue) {
      dispatch(setSelectedDataAccuracy(searchValue));
    }
    if (displayParam === "Classic" || displayParam === "Extended") {
      dispatch(setDisplayMode(displayParam));
    }

    // Handle hideDone parameter
    if (doneIssuesParam === "hideDone" || doneIssuesParam === "showDone") {
      dispatch(setHideDone(doneIssuesParam));
    } else if (!doneIssuesParam) {
      // If no parameter exists on first load, set default to hideDone
      dispatch(setHideDone("hideDone"));
      setSearchParams((prevParams) => {
        const updatedParams = Object.fromEntries(prevParams);
        updatedParams.doneIssues = "hideDone";
        return updatedParams;
      });
    }
  }, [searchValue, displayParam, doneIssuesParam, dispatch, setSearchParams]);

  const handleToggle = (event: any) => {
    const isExtended = event.target.checked;
    const newDisplayMode = isExtended ? "Extended" : "Classic";

    // Update Redux store
    dispatch(setDisplayMode(newDisplayMode));

    // Update URL search params
    setSearchParams((prevParams) => {
      const updatedParams = Object.fromEntries(prevParams);
      updatedParams.display = newDisplayMode;
      return updatedParams;
    });
  };

  const handleToggleHide = (event: any) => {
    const isHide = event.target.checked;
    const newHideValue = isHide ? "hideDone" : "showDone";

    // Update Redux store
    dispatch(setHideDone(newHideValue));

    // Update URL with a separate param 'doneIssues'
    setSearchParams((prevParams) => {
      const updatedParams = Object.fromEntries(prevParams);
      updatedParams.doneIssues = newHideValue; // separate key
      return updatedParams;
    });
  };

  return (
    <>
      <Stack
        flexDirection={"row"}
        // justifyContent={"flex-end"}
        gap={2}
        data-tracker="166"
      >
        <Stack flexDirection={"column"} gap={2} alignItems={"flex-start"}>
          Anomalies List
          <Autocomplete
            sx={{ width: "300px" }}
            options={Array.from(
              new Set(
                (dataAccruracyData?.query_output || [])
                  .map((item: any) => item.title)
                  .filter(
                    (title: string | null) => title !== null && title !== "",
                  ),
              ),
            )}
            getOptionLabel={(option) => option || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Discrepancy Type"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
                color="secondary"
              />
            )}
            size="small"
            onChange={(_, newValue) => {
              dispatch(setSelectedDataAccuracy(newValue));
              setSearchParams((prevParams) => {
                const updatedParams = Object.fromEntries(prevParams); // Preserve previous params
                if (newValue) {
                  updatedParams.search = newValue; // Set new value
                } else {
                  delete updatedParams.search; // Remove from URL if cleared
                }
                updatedParams.page = "0";

                return updatedParams;
              });
            }}
            value={selectedDataAccuracy}
          />
        </Stack>

        <Stack
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"flex-end"}
          sx={{ width: "100%" }}
          data-tracker="166"
        >
          <DataAccuracyFilter
            clearSelectedDataAccuracy={clearSelectedDataAccuracy}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mt: { xs: 2, md: 0 },
            }}
          >
            <FormControlLabel
              label={<Typography>Hide Done</Typography>}
              labelPlacement="start" // label on left, switch on right
              control={
                <Switch
                  color="secondary"
                  size="small"
                  checked={hideDone === "hideDone"}
                  onChange={handleToggleHide}
                />
              }
              sx={{
                width: "100%",
                justifyContent: "space-between",
                m: 0,
                paddingBlock: "8px",
              }}
            />
            <FormControlLabel
              label={<Typography>Extended View</Typography>}
              labelPlacement="start" // puts label on left, switch on right
              control={
                <Switch
                  color="secondary"
                  size="small"
                  checked={displayMode === "Extended"}
                  onChange={handleToggle}
                />
              }
              sx={{
                width: "100%",
                justifyContent: "space-between",
                m: 0,
                paddingBlock: "8px",
              }}
            />
          </Box>
        </Stack>
      </Stack>
    </>
  );
}
export default DataAccuracyFilterCard;
